import { Component, EventEmitter, Input, Output } from '@angular/core';
import { RendererField, RendererFieldType, RendererLevel, RendererSelectedNode } from '../../models/renderer.models';

/**
 * Renders a realistic phone mockup showing the fields and child levels of the
 * currently selected form level.
 *
 * The host component owns the `selectedNode` state. Both child-level rows
 * inside the phone and the structure tree can emit a new selection; the host
 * must respond by updating `selectedNode` and propagating `fields`, `children`,
 * and `infoBarText` accordingly.
 *
 * Usage:
 * ```html
 * <lib-phone-preview
 *   [title]="template.name"
 *   [fields]="currentFields"
 *   [children]="currentChildren"
 *   [selectedNode]="selectedNode"
 *   [infoBarText]="selectedNode?.title"
 *   (nodeSelected)="selectedNode = $event">
 * </lib-phone-preview>
 * ```
 */
@Component({
  selector: 'lib-phone-preview',
  templateUrl: './phone-preview.component.html',
  styleUrls: ['./phone-preview.component.scss'],
})
export class PhonePreviewComponent {
  /** Shown in the phone's app-header bar. */
  @Input() title = '';

  /** Fields to render inside the phone content area. */
  @Input() fields: RendererField[] = [];

  /** Child levels shown as navigation rows below the fields. */
  @Input() children: RendererLevel[] = [];

  /** Used to highlight the selected child level row. */
  @Input() selectedNode: RendererSelectedNode | null = null;

  /** Text shown in the green info bar at the top of the content area. */
  @Input() infoBarText = '';

  /** Emitted when the user taps a child-level row inside the phone. */
  @Output() nodeSelected = new EventEmitter<RendererSelectedNode>();

  readonly FieldType = RendererFieldType;
  readonly today = new Date();

  isSelected(level: RendererLevel | null | undefined): boolean {
    return !!level && this.selectedNode?.level === level;
  }

  selectChild(child: RendererLevel): void {
    this.nodeSelected.emit({ level: child, title: child.name ?? 'Ebene' });
  }
}
